import { useState, useCallback, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Bold, Italic, Underline, Strikethrough,
} from "lucide-react";

/**
 * Custom style panels for the GrapeJS email editor.
 * Replaces the default GrapeJS StyleManager with a clean, branded UI.
 */

// ── Reusable mini-components ──────────────────────────────────────────────────
function SectionTitle({ children }) {
  return <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">{children}</p>;
}

function StyleRow({ label, children }) {
  return (
    <div className="flex items-center gap-2 min-h-[32px]">
      <span className="text-[11px] text-slate-400 w-16 flex-shrink-0">{label}</span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function MiniInput({ value, onChange, suffix, placeholder, type = "text" }) {
  // Strip unit suffix for display so user sees only the number
  const displayVal = suffix && value ? String(value).replace(suffix, "").trim() : (value || "");

  const handleChange = (raw) => {
    if (suffix && raw && /^-?\d*\.?\d*$/.test(raw)) {
      onChange(raw + suffix);
    } else {
      onChange(raw);
    }
  };

  return (
    <div className="flex items-center bg-slate-50 border border-slate-200 rounded-md overflow-hidden">
      <input
        type={type}
        value={displayVal}
        onChange={e => handleChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-2 py-1.5 text-[11px] text-slate-700 bg-transparent outline-none"
      />
      {suffix && <span className="text-[10px] text-slate-400 pr-2 flex-shrink-0">{suffix}</span>}
    </div>
  );
}

function ColorInput({ value, onChange }) {
  return (
    <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-md p-1">
      <input
        type="color"
        value={value || "#000000"}
        onChange={e => onChange(e.target.value)}
        className="w-6 h-6 rounded cursor-pointer border-0"
        style={{ padding: 0 }}
      />
      <span className="text-[10px] text-slate-500 font-mono">{value || "—"}</span>
    </div>
  );
}

function ToggleGroup({ options, value, onChange, themeColor }) {
  return (
    <div className="flex gap-0.5">
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          title={opt.title}
          className={`flex-1 h-8 flex items-center justify-center rounded-md text-xs transition-colors ${
            value === opt.value
              ? "text-white border"
              : "bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100"
          }`}
          style={value === opt.value ? { backgroundColor: themeColor, borderColor: themeColor } : {}}
        >
          {opt.icon ? <opt.icon className="w-3.5 h-3.5" /> : opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function parseStyleValue(val) {
  if (!val) return "";
  return String(val).replace(/"/g, "");
}

function rgbToHex(rgb) {
  if (!rgb || rgb.startsWith("#")) return rgb;
  const match = rgb.match(/\d+/g);
  if (!match || match.length < 3) return rgb;
  const hex = match.slice(0, 3).map(n => parseInt(n).toString(16).padStart(2, "0")).join("");
  return `#${hex}`;
}

// ── Main StylePanels Component ────────────────────────────────────────────────
export default function StylePanels({ editorRef, tc500 }) {
  const themeColor = tc500 || "#F43F5E";
  const [styles, setStyles] = useState({});
  const [hasSelection, setHasSelection] = useState(false);

  // Read styles from selected component
  const readStyles = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const selected = editor.getSelected();
    if (!selected) {
      setHasSelection(false);
      return;
    }
    setHasSelection(true);
    const s = selected.getStyle();
    setStyles({
      fontSize: parseStyleValue(s["font-size"]),
      fontWeight: parseStyleValue(s["font-weight"]),
      fontFamily: parseStyleValue(s["font-family"]),
      color: rgbToHex(parseStyleValue(s["color"])),
      textAlign: parseStyleValue(s["text-align"]),
      lineHeight: parseStyleValue(s["line-height"]),
      letterSpacing: parseStyleValue(s["letter-spacing"]),
      textDecoration: parseStyleValue(s["text-decoration"]),
      fontStyle: parseStyleValue(s["font-style"]),
      width: parseStyleValue(s["width"]),
      height: parseStyleValue(s["height"]),
      maxWidth: parseStyleValue(s["max-width"]),
      paddingTop: parseStyleValue(s["padding-top"]),
      paddingRight: parseStyleValue(s["padding-right"]),
      paddingBottom: parseStyleValue(s["padding-bottom"]),
      paddingLeft: parseStyleValue(s["padding-left"]),
      padding: parseStyleValue(s["padding"]),
      marginTop: parseStyleValue(s["margin-top"]),
      marginRight: parseStyleValue(s["margin-right"]),
      marginBottom: parseStyleValue(s["margin-bottom"]),
      marginLeft: parseStyleValue(s["margin-left"]),
      margin: parseStyleValue(s["margin"]),
      borderRadius: parseStyleValue(s["border-radius"]),
      borderWidth: parseStyleValue(s["border-width"]),
      borderStyle: parseStyleValue(s["border-style"]),
      borderColor: rgbToHex(parseStyleValue(s["border-color"])),
      opacity: parseStyleValue(s["opacity"]),
      backgroundColor: rgbToHex(parseStyleValue(s["background-color"])),
    });
  }, [editorRef]);

  // Listen to selection changes — poll for editor readiness since ref.current
  // is set after the parent's useEffect (child effects run first in React).
  useEffect(() => {
    let bound = false;
    let editorInstance = null;
    const handler = () => readStyles();
    const deselect = () => { setHasSelection(false); setStyles({}); };

    const bind = () => {
      const editor = editorRef.current;
      if (!editor || bound) return;
      bound = true;
      editorInstance = editor;
      editor.on("component:selected", handler);
      editor.on("component:deselected", deselect);
      editor.on("component:styleUpdate", handler);
      // If something is already selected, read it
      if (editor.getSelected()) handler();
    };

    bind();
    const interval = !bound ? setInterval(bind, 150) : null;

    return () => {
      if (interval) clearInterval(interval);
      if (editorInstance) {
        editorInstance.off("component:selected", handler);
        editorInstance.off("component:deselected", deselect);
        editorInstance.off("component:styleUpdate", handler);
      }
    };
  }, [editorRef, readStyles]);

  // Apply a CSS property to the selected element
  const applyStyle = useCallback((prop, value) => {
    const editor = editorRef.current;
    if (!editor) return;
    const selected = editor.getSelected();
    if (!selected) { toast.error("Selecione um elemento primeiro."); return; }
    selected.addStyle({ [prop]: value });
    setStyles(prev => ({ ...prev, [prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase())]: value }));
  }, [editorRef]);

  const applyPadding = useCallback((val) => {
    const editor = editorRef.current;
    if (!editor) return;
    const selected = editor.getSelected();
    if (!selected) return;
    selected.addStyle({
      "padding-top": val, "padding-right": val, "padding-bottom": val, "padding-left": val
    });
    setStyles(prev => ({ ...prev, paddingTop: val, paddingRight: val, paddingBottom: val, paddingLeft: val }));
  }, [editorRef]);

  const applyMargin = useCallback((val) => {
    const editor = editorRef.current;
    if (!editor) return;
    const selected = editor.getSelected();
    if (!selected) return;
    selected.addStyle({
      "margin-top": val, "margin-right": val, "margin-bottom": val, "margin-left": val
    });
    setStyles(prev => ({ ...prev, marginTop: val, marginRight: val, marginBottom: val, marginLeft: val }));
  }, [editorRef]);

  if (!hasSelection) {
    return (
      <div className="p-4 text-center">
        <p className="text-xs text-slate-400 mt-8">Clique em um elemento no canvas para editar seus estilos.</p>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-5">
      {/* ── TYPOGRAPHY ──────────────────────────────────── */}
      <div>
        <SectionTitle>Tipografia</SectionTitle>
        <div className="space-y-2.5">
          <StyleRow label="Cor">
            <ColorInput value={styles.color} onChange={v => applyStyle("color", v)} />
          </StyleRow>
          <StyleRow label="Tamanho">
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="8" max="72" step="1"
                value={parseInt(styles.fontSize) || 16}
                onChange={e => applyStyle("font-size", e.target.value + "px")}
                className="flex-1 h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: themeColor }}
              />
              <span className="text-[10px] text-slate-500 w-10 text-right">{parseInt(styles.fontSize) || 16}px</span>
            </div>
          </StyleRow>
          <StyleRow label="Peso">
            <ToggleGroup
              value={styles.fontWeight}
              onChange={v => applyStyle("font-weight", v)}
              themeColor={themeColor}
              options={[
                { value: "400", label: "Normal" },
                { value: "700", label: "Bold" },
              ]}
            />
          </StyleRow>
          <StyleRow label="Alinhamento">
            <ToggleGroup
              value={styles.textAlign}
              onChange={v => applyStyle("text-align", v)}
              themeColor={themeColor}
              options={[
                { value: "left", icon: AlignLeft, title: "Esquerda" },
                { value: "center", icon: AlignCenter, title: "Centro" },
                { value: "right", icon: AlignRight, title: "Direita" },
                { value: "justify", icon: AlignJustify, title: "Justificado" },
              ]}
            />
          </StyleRow>
          <StyleRow label="Altura linha">
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0.8" max="3" step="0.1"
                value={parseFloat(styles.lineHeight) || 1.6}
                onChange={e => applyStyle("line-height", e.target.value)}
                className="flex-1 h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: themeColor }}
              />
              <span className="text-[10px] text-slate-500 w-8 text-right">{parseFloat(styles.lineHeight) || 1.6}</span>
            </div>
          </StyleRow>
          <StyleRow label="Espaçamento">
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0" max="10" step="0.5"
                value={parseFloat(styles.letterSpacing) || 0}
                onChange={e => applyStyle("letter-spacing", e.target.value + "px")}
                className="flex-1 h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: themeColor }}
              />
              <span className="text-[10px] text-slate-500 w-10 text-right">{parseFloat(styles.letterSpacing) || 0}px</span>
            </div>
          </StyleRow>
          <StyleRow label="Estilo">
            <div className="flex gap-0.5">
              {[
                { prop: "font-weight", val: "700", current: styles.fontWeight, icon: Bold, title: "Negrito" },
                { prop: "font-style", val: "italic", current: styles.fontStyle, icon: Italic, title: "Itálico" },
                { prop: "text-decoration", val: "underline", current: styles.textDecoration, icon: Underline, title: "Sublinhado" },
                { prop: "text-decoration", val: "line-through", current: styles.textDecoration, icon: Strikethrough, title: "Tachado" },
              ].map((btn, i) => (
                <button key={i} title={btn.title}
                  onClick={() => applyStyle(btn.prop, btn.current === btn.val ? (btn.prop === "font-weight" ? "400" : "none") : btn.val)}
                  className={`flex-1 h-8 flex items-center justify-center rounded-md transition-colors ${
                    btn.current === btn.val
                      ? "text-white border"
                      : "bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100"
                  }`}
                  style={btn.current === btn.val ? { backgroundColor: themeColor, borderColor: themeColor } : {}}>
                  <btn.icon className="w-3.5 h-3.5" />
                </button>
              ))}
            </div>
          </StyleRow>
          <StyleRow label="Fonte">
            <select
              value={styles.fontFamily || ""}
              onChange={e => applyStyle("font-family", e.target.value)}
              className="w-full px-2 py-1.5 text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded-md outline-none"
            >
              <option value="">Padrão</option>
              <option value="Arial,Helvetica,sans-serif">Arial</option>
              <option value="'Segoe UI',Arial,sans-serif">Segoe UI</option>
              <option value="Georgia,serif">Georgia</option>
              <option value="'Times New Roman',serif">Times New Roman</option>
              <option value="Verdana,sans-serif">Verdana</option>
              <option value="'Courier New',monospace">Courier New</option>
              <option value="Tahoma,sans-serif">Tahoma</option>
              <option value="'Trebuchet MS',sans-serif">Trebuchet MS</option>
            </select>
          </StyleRow>
        </div>
      </div>

      {/* ── DIMENSIONS ──────────────────────────────────── */}
      <div>
        <SectionTitle>Dimensões</SectionTitle>
        <div className="space-y-2.5">
          <StyleRow label="Largura">
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0" max="900" step="10"
                value={parseInt(styles.width) || 0}
                onChange={e => applyStyle("width", e.target.value + "px")}
                className="flex-1 h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: themeColor }}
              />
              <span className="text-[10px] text-slate-500 w-10 text-right">{parseInt(styles.width) || "auto"}</span>
            </div>
          </StyleRow>
          <StyleRow label="Altura">
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0" max="900" step="10"
                value={parseInt(styles.height) || 0}
                onChange={e => applyStyle("height", e.target.value + "px")}
                className="flex-1 h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: themeColor }}
              />
              <span className="text-[10px] text-slate-500 w-10 text-right">{parseInt(styles.height) || "auto"}</span>
            </div>
          </StyleRow>
          <StyleRow label="Larg. máx.">
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="100" max="900" step="10"
                value={parseInt(styles.maxWidth) || 600}
                onChange={e => applyStyle("max-width", e.target.value + "px")}
                className="flex-1 h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: themeColor }}
              />
              <span className="text-[10px] text-slate-500 w-12 text-right">{parseInt(styles.maxWidth) || 600}px</span>
            </div>
          </StyleRow>

          {/* Padding */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] text-slate-400">Padding (interno)</label>
              <div className="flex gap-1">
                {["8px", "16px", "24px", "32px", "48px"].map(v => (
                  <button key={v} onClick={() => applyPadding(v)}
                    className="px-1.5 py-0.5 text-[9px] bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 rounded transition-colors">
                    {parseInt(v)}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-1">
              {[
                { key: "paddingTop", prop: "padding-top", label: "↑" },
                { key: "paddingRight", prop: "padding-right", label: "→" },
                { key: "paddingBottom", prop: "padding-bottom", label: "↓" },
                { key: "paddingLeft", prop: "padding-left", label: "←" },
              ].map(p => (
                <div key={p.key}>
                  <span className="text-[9px] text-slate-400 text-center block mb-0.5">{p.label}</span>
                  <MiniInput value={styles[p.key]} onChange={v => applyStyle(p.prop, v)} placeholder="0" suffix="px" />
                </div>
              ))}
            </div>
          </div>

          {/* Margin */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] text-slate-400">Margin (externo)</label>
              <div className="flex gap-1">
                {["0", "8px", "16px", "auto"].map(v => (
                  <button key={v} onClick={() => applyMargin(v)}
                    className="px-1.5 py-0.5 text-[9px] bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 rounded transition-colors">
                    {v === "auto" ? "auto" : parseInt(v)}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-1">
              {[
                { key: "marginTop", prop: "margin-top", label: "↑" },
                { key: "marginRight", prop: "margin-right", label: "→" },
                { key: "marginBottom", prop: "margin-bottom", label: "↓" },
                { key: "marginLeft", prop: "margin-left", label: "←" },
              ].map(p => (
                <div key={p.key}>
                  <span className="text-[9px] text-slate-400 text-center block mb-0.5">{p.label}</span>
                  <MiniInput value={styles[p.key]} onChange={v => applyStyle(p.prop, v)} placeholder="0" suffix="px" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── DECORATION ──────────────────────────────────── */}
      <div>
        <SectionTitle>Decoração</SectionTitle>
        <div className="space-y-2.5">
          <StyleRow label="Arredond.">
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0" max="50" step="1"
                value={parseInt(styles.borderRadius) || 0}
                onChange={e => applyStyle("border-radius", e.target.value + "px")}
                className="flex-1 h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: themeColor }}
              />
              <span className="text-[10px] text-slate-500 w-10 text-right">{parseInt(styles.borderRadius) || 0}px</span>
            </div>
          </StyleRow>

          <StyleRow label="Borda">
            <div className="flex gap-1">
              <MiniInput value={styles.borderWidth} onChange={v => applyStyle("border-width", v)} placeholder="0" suffix="px" />
              <select value={styles.borderStyle || ""} onChange={e => applyStyle("border-style", e.target.value)}
                className="px-1.5 py-1 text-[10px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md outline-none">
                <option value="">Nenhum</option>
                <option value="solid">Sólido</option>
                <option value="dashed">Tracejado</option>
                <option value="dotted">Pontilhado</option>
                <option value="double">Duplo</option>
              </select>
            </div>
          </StyleRow>
          <StyleRow label="Cor borda">
            <ColorInput value={styles.borderColor} onChange={v => applyStyle("border-color", v)} />
          </StyleRow>

          <StyleRow label="Cor fundo">
            <ColorInput value={styles.backgroundColor} onChange={v => applyStyle("background-color", v)} />
          </StyleRow>

          <StyleRow label="Opacidade">
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0" max="1" step="0.05"
                value={styles.opacity || 1}
                onChange={e => applyStyle("opacity", e.target.value)}
                className="flex-1 h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: themeColor }}
              />
              <span className="text-[10px] text-slate-500 w-8 text-right">{Math.round((styles.opacity || 1) * 100)}%</span>
            </div>
          </StyleRow>
        </div>
      </div>
    </div>
  );
}
